// src/components/common/SecuritySettingsModal.tsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  ShieldCheckIcon, 
  KeyIcon, 
  LockClosedIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ChartBarIcon,
  ClockIcon,
  ComputerDesktopIcon,
  ArrowRightOnRectangleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import authService from '../../features/auth/services/authService';
import TwoFactorSetupWizard from '../../features/auth/components/TwoFactorSetupWizard';
import AuditLog from '../../features/auth/components/AuditLog';
import { TwoFactorStatusResponse, RateLimitStatusResponse } from '../../features/auth/types';

interface SecuritySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SecuritySettingsModal: React.FC<SecuritySettingsModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'overview' | 'audit'>('overview');
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatusResponse | null>(null);
  const [rateLimitStatus, setRateLimitStatus] = useState<RateLimitStatusResponse | null>(null);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch user info to check if user is staff
  const [userInfo, setUserInfo] = useState<any>(null);
  
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const data = await authService.getUserInfo();
        setUserInfo(data);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    };
    
    if (isOpen) {
      fetchUserInfo();
    }
  }, [isOpen]);
  

  // Password change form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSecurityOverview();
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const loadSecurityOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [twoFactorResponse, rateLimitResponse] = await Promise.allSettled([
        authService.get2FAStatus(),
        authService.getRateLimitStatus()
      ]);

      if (twoFactorResponse.status === 'fulfilled') {
        setTwoFactorStatus(twoFactorResponse.value);
      }

      if (rateLimitResponse.status === 'fulfilled') {
        setRateLimitStatus(rateLimitResponse.value);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load security overview');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAllDevices = async () => {
    if (!window.confirm('Are you sure you want to log out of all devices? You will need to log in again.')) {
      return;
    }

    try {
      await authService.enhancedLogout({ logout_type: 'all_devices' });
      // This would typically redirect to login page
      window.location.href = '/auth';
    } catch (err: any) {
      setError(err.message || 'Failed to logout all devices');
    }
  };

  const handleSetup2FA = () => {
    setShowSetupWizard(true);
  };

  const handle2FASetupComplete = () => {
    setShowSetupWizard(false);
    setSuccess('Two-Factor Authentication has been successfully enabled!');
    // Reload security overview to get updated status
    loadSecurityOverview();
  };

  const handle2FASetupCancel = () => {
    setShowSetupWizard(false);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    try {
      setPasswordLoading(true);
      
      await authService.changePassword({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      
      setSuccess('Password changed successfully!');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordChange(false);
    } catch (err: any) {
      console.error('Password change error:', err);
      setError(err?.message || err?.error || 'Failed to change password. Please check your current password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative bg-theme-bg dark:bg-dark-theme-bg rounded-lg shadow-2xl w-[95vw] h-[85vh] max-w-6xl overflow-hidden border border-theme-border dark:border-dark-theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col md:flex-row h-full">
          {/* Sidebar */}
          <div className="w-full md:w-64 bg-theme-bg dark:bg-dark-theme-bg md:rounded-l-lg p-4 md:p-6 flex-shrink-0 border-b md:border-b-0 md:border-r border-theme-border dark:border-dark-theme-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                Security
              </h2>
              <button
                onClick={onClose}
                className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text"
                aria-label="Close security settings"
                title="Close security settings modal"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <nav className="space-y-2">
              {[
                { id: 'overview', label: 'Overview', icon: ShieldCheckIcon },
                // { id: '2fa', label: '2FA', icon: KeyIcon },
                // { id: 'password', label: 'Password', icon: LockClosedIcon },
                ...(userInfo?.user?.is_staff ? [{ id: 'audit', label: 'Audit Log', icon: ChartBarIcon }] : [])
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSection(tab.id as any)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left transition-colors ${
                      activeSection === tab.id
                        ? 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark'
                        : 'text-theme-text-secondary dark:text-dark-theme-text-secondary hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium text-sm md:text-base">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-4 md:p-6 overflow-y-auto">
            {/* Success/Error Messages */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  <p className="text-green-700 dark:text-green-300">{success}</p>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                    <p className="text-red-700 dark:text-red-300">{error}</p>
                  </div>
                  {error.includes('Failed to load security') && (
                    <button
                      onClick={() => {
                        setError(null);
                        loadSecurityOverview();
                      }}
                      className="ml-4 px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Overview Section */}
            {activeSection === 'overview' && (
              <div className="space-y-6">
                {!showPasswordChange ? (
                  <>
                    <h3 className="text-2xl font-semibold text-theme-text dark:text-dark-theme-text">
                      Security Overview
                    </h3>

                    {loading ? (
                      <div className="flex items-center justify-center p-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-light"></div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 md:gap-6">
                        {/* Two-Factor Authentication Status */}
                        <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-4 md:p-6 border border-theme-border dark:border-dark-theme-border">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <ShieldCheckIcon className={`h-8 w-8 ${twoFactorStatus?.is_enabled ? 'text-green-500' : 'text-yellow-500'}`} />
                              <div>
                                <h4 className="font-semibold text-theme-text dark:text-dark-theme-text">
                                  Two-Factor Authentication
                                </h4>
                                <p className={`text-sm ${twoFactorStatus?.is_enabled ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                                  {twoFactorStatus?.is_enabled ? 'Enabled' : 'Disabled'}
                                </p>
                              </div>
                            </div>
                          </div>
                          {twoFactorStatus?.is_enabled && (
                            <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary mb-3">
                              Backup codes remaining: {twoFactorStatus.backup_codes_remaining || 0}
                            </p>
                          )}
                          <button
                            onClick={twoFactorStatus?.is_enabled ? () => setShowSetupWizard(true) : handleSetup2FA}
                            className="w-full py-2 px-4 bg-primary-light dark:bg-primary-dark text-white rounded-md hover:opacity-90 transition-opacity"
                          >
                            {twoFactorStatus?.is_enabled ? 'Manage 2FA' : 'Enable 2FA'}
                          </button>
                        </div>

                        {/* Rate Limiting Status */}
                        {/* <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-4 md:p-6 border border-theme-border dark:border-dark-theme-border">
                          <div className="flex items-center space-x-3 mb-4">
                            <ClockIcon className="h-8 w-8 text-blue-500" />
                            <div>
                              <h4 className="font-semibold text-theme-text dark:text-dark-theme-text">
                                Rate Limiting
                              </h4>
                              <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
                                Login attempt protection
                              </p>
                            </div>
                          </div>
                          {rateLimitStatus && (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-theme-text-secondary dark:text-dark-theme-text-secondary">Remaining attempts:</span>
                                <span className="text-theme-text dark:text-dark-theme-text font-medium">{rateLimitStatus.remaining_attempts}/{rateLimitStatus.limit}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-theme-text-secondary dark:text-dark-theme-text-secondary">Window:</span>
                                <span className="text-theme-text dark:text-dark-theme-text font-medium">{rateLimitStatus.window_seconds}s</span>
                              </div>
                              {rateLimitStatus.remaining_attempts < rateLimitStatus.limit && (
                                <div className="flex justify-between">
                                  <span className="text-theme-text-secondary dark:text-dark-theme-text-secondary">Reset time:</span>
                                  <span className="text-theme-text dark:text-dark-theme-text font-medium">
                                    {new Date(rateLimitStatus.reset_time).toLocaleTimeString()}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div> */}

                        {/* Quick Actions */}
                        <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-4 md:p-6 border border-theme-border dark:border-dark-theme-border">
                          <h4 className="font-semibold text-theme-text dark:text-dark-theme-text mb-4">
                            Access Management
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => setShowPasswordChange(true)}
                              className="w-full py-2 px-4 border border-theme-border dark:border-dark-theme-border rounded-md text-sm font-medium text-theme-text dark:text-dark-theme-text hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary"
                            >
                              Change Password
                            </button>
                            {userInfo?.user?.is_staff && (
                              <button
                                onClick={() => setActiveSection('audit')}
                                className="w-full py-2 px-4 border border-theme-border dark:border-dark-theme-border rounded-md text-sm font-medium text-theme-text dark:text-dark-theme-text hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary"
                              >
                                View Audit Log
                              </button>
                            )}
                            <button
                              onClick={handleLogoutAllDevices}
                              className="w-full py-2 px-4 border border-red-300 dark:border-red-700 rounded-md text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              Logout All Devices
                            </button>
                          </div>
                        </div>

                        {/* Security Score */}
                        {/* <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-4 md:p-6 border border-theme-border dark:border-dark-theme-border">
                          <h4 className="font-semibold text-theme-text dark:text-dark-theme-text mb-4">
                            Security Score
                          </h4>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">2FA Enabled</span>
                              <span className={`text-sm font-medium ${twoFactorStatus?.is_enabled ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {twoFactorStatus?.is_enabled ? '✓' : '✗'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">Strong Password</span>
                              <span className="text-sm font-medium text-green-600 dark:text-green-400">✓</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">Rate Limiting</span>
                              <span className="text-sm font-medium text-green-600 dark:text-green-400">✓</span>
                            </div>
                            <div className="mt-4 pt-3 border-t border-theme-border dark:border-dark-theme-border">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-theme-text dark:text-dark-theme-text">Overall Score</span>
                                <span className={`font-bold ${twoFactorStatus?.is_enabled ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                                  {twoFactorStatus?.is_enabled ? '95%' : '75%'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div> */}
                      </div>
                    )}
                  </>
                ) : (
                  /* Password Change Form - replaces overview content */
                  <div>
                    <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-6 border border-theme-border dark:border-dark-theme-border">
                      <div className="flex items-center space-x-3 mb-4">
                        <KeyIcon className="h-6 w-6 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                        <h4 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                          Change Password
                        </h4>
                      </div>

                      <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary mb-6">
                        Keep your account secure by using a strong, unique password.
                      </p>

                      <form onSubmit={handlePasswordChange} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                            Current Password
                          </label>
                          <div className="relative">
                            <input
                              type={showCurrentPassword ? 'text' : 'password'}
                              value={passwordForm.currentPassword}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                              required
                              className="w-full px-4 py-2 pr-10 border border-theme-border dark:border-dark-theme-border rounded-md bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent"
                              placeholder="Enter your current password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
                              aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                            >
                              {showCurrentPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                            New Password
                          </label>
                          <div className="relative">
                            <input
                              type={showNewPassword ? 'text' : 'password'}
                              value={passwordForm.newPassword}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                              required
                              className="w-full px-4 py-2 pr-10 border border-theme-border dark:border-dark-theme-border rounded-md bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent"
                              placeholder="Enter your new password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
                              aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                            >
                              {showNewPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                            Confirm New Password
                          </label>
                          <div className="relative">
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              value={passwordForm.confirmPassword}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                              required
                              className="w-full px-4 py-2 pr-10 border border-theme-border dark:border-dark-theme-border rounded-md bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent"
                              placeholder="Confirm your new password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
                              aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                            >
                              {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          <button
                            type="submit"
                            disabled={passwordLoading}
                            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors duration-150"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            <span>{passwordLoading ? 'Changing...' : 'Change Password'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowPasswordChange(false);
                              setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                              setError(null);
                            }}
                            disabled={passwordLoading}
                            className="px-4 py-2 bg-neutral-500 text-white rounded-md hover:bg-neutral-600 disabled:opacity-50 transition-colors duration-150"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 2FA Section - COMMENTED OUT 
            {activeSection === '2fa' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-theme-text dark:text-dark-theme-text">
                  Two-Factor Authentication
                </h3>
                
                <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-6 border border-theme-border dark:border-dark-theme-border">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <ShieldCheckIcon className="h-6 w-6 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                      <h4 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                        Authentication Status
                      </h4>
                    </div>
                    
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      twoFactorStatus?.is_enabled 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                    }`}>
                      {twoFactorStatus?.is_enabled ? 'Enabled' : 'Not Enabled'}
                    </span>
                  </div>

                  <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary mb-6">
                    {twoFactorStatus?.is_enabled 
                      ? 'Your account is protected with two-factor authentication. You\'ll need your authenticator app to sign in.'
                      : 'Add an extra layer of security to your account by enabling two-factor authentication.'
                    }
                  </p>

                  {!twoFactorStatus?.is_enabled ? (
                    <button
                      onClick={handleSetup2FA}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-light dark:bg-primary-dark text-white rounded-md hover:opacity-90 transition-opacity duration-150"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Enable 2FA</span>
                    </button>
                  ) : (
                    <div className="space-y-4">
                      {twoFactorStatus.backup_codes_remaining !== undefined && (
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            You have <strong>{twoFactorStatus.backup_codes_remaining}</strong> backup codes remaining.
                          </p>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => setShowSetupWizard(true)}
                          className="flex items-center space-x-2 px-4 py-2 bg-neutral-500 text-white rounded-md hover:bg-neutral-600 transition-colors duration-150"
                        >
                          <Cog6ToothIcon className="h-4 w-4" />
                          <span>Manage 2FA</span>
                        </button>
                        <p className="text-sm text-theme-text-tertiary dark:text-dark-theme-text-tertiary">
                          View backup codes, disable 2FA, or regenerate codes
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            */}

            {/* Password Section - COMMENTED OUT 
            {activeSection === 'password' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-theme-text dark:text-dark-theme-text">
                  Password Management
                </h3>
                
                <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-6 border border-theme-border dark:border-dark-theme-border">
                  <div className="flex items-center space-x-3 mb-4">
                    <KeyIcon className="h-6 w-6 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                    <h4 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                      Change Password
                    </h4>
                  </div>

                  <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary mb-6">
                    Keep your account secure by using a strong, unique password.
                  </p>

                  {!showPasswordChange ? (
                    <button
                      onClick={() => setShowPasswordChange(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-light dark:bg-primary-dark text-white rounded-md hover:opacity-90 transition-opacity duration-150"
                    >
                      <LockClosedIcon className="h-4 w-4" />
                      <span>Change Password</span>
                    </button>
                  ) : (
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                          Current Password
                        </label>
                        <div className="relative">
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                            required
                            className="w-full px-4 py-2 pr-10 border border-theme-border dark:border-dark-theme-border rounded-md bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent"
                            placeholder="Enter your current password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
                            aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                          >
                            {showCurrentPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                            required
                            className="w-full px-4 py-2 pr-10 border border-theme-border dark:border-dark-theme-border rounded-md bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent"
                            placeholder="Enter your new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
                            aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                          >
                            {showNewPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            required
                            className="w-full px-4 py-2 pr-10 border border-theme-border dark:border-dark-theme-border rounded-md bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent"
                            placeholder="Confirm your new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
                            aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                          >
                            {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <button
                          type="submit"
                          disabled={passwordLoading}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors duration-150"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          <span>{passwordLoading ? 'Changing...' : 'Change Password'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowPasswordChange(false);
                            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                            setError(null);
                          }}
                          disabled={passwordLoading}
                          className="px-4 py-2 bg-neutral-500 text-white rounded-md hover:bg-neutral-600 disabled:opacity-50 transition-colors duration-150"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}
            */}

            {/* Audit Log Section */}
            {activeSection === 'audit' && userInfo?.user?.is_staff && (
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-theme-text dark:text-dark-theme-text">
                  Audit Log
                </h3>
                
                <div className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg border border-theme-border dark:border-dark-theme-border">
                  <AuditLog />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 2FA Setup Wizard Modal */}
        {showSetupWizard && (
          <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="relative bg-theme-bg dark:bg-dark-theme-bg rounded-lg shadow-2xl w-full max-w-2xl mx-4 my-8 max-h-[calc(100vh-4rem)] overflow-y-auto border border-theme-border dark:border-dark-theme-border">
              <TwoFactorSetupWizard
                isOpen={showSetupWizard}
                onClose={handle2FASetupCancel}
                onComplete={handle2FASetupComplete}
              />
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default SecuritySettingsModal;